import { UserFeatureResponseModel } from "api/merchantRepository/features/model/UserFeatureResponseModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { AppStartUpInjection } from "app/startup/di/AppStartUpInjection";
import { AppStartUpRequestEntity } from "app/startup/domain/entities/AppStartUpRequestEntity";
import { AppStartUpResponseEntity } from "app/startup/domain/entities/AppStartUpResponseEntity";
import { SidebarMenuConfigModel } from "components/sidebar/data/models/SidebarMenuConfigModel";
import { LoginResponseModel } from "features/login/data/model/responseModel/LoginResponseModel";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import {
  Context,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "reflect-metadata";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { UserRoleEnum } from "utils/enums/UserRoleEnum";
import { ActionFormat } from "utils/format/ActionFormat";
import { DataFormat } from "utils/format/DataFormat";
import { findFeature } from "utils/format/FeatureFormat";
import { StringFormat } from "utils/format/StringFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import useScreenSize from "utils/responsive/CheckScreenSize";
import packageJson from "../../package.json";
import { IContextModel, IFetchDataWithFeature } from "./model/ContextModel";
import { AppBasedServiceInjection } from "utils/serviceRepo/ServiceRepo";

export const LocalContext: Context<IContextModel | undefined> =
  createContext(undefined);

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const { isLargeScreen } = useScreenSize();
  const [user, setUser] = useState<LoginResponseModel>();
  const versionNumber = packageJson?.version || "";
  const [partnerList, setPartnerList] = useState<
    PartnerManagementResponseModel[]
  >([]);
  const [globalLoading, setGlobalLoading] = useState<string>("");
  const history = useNavigate();
  const location = useLocation();
  const [info, setInfo] = useState("");
  const token = DataFormat.getToken();
  const [config, setConfig] = useState({});
  const [language, setLanguage] = useState<string>("vi");
  const [userFeature, setUserFeature] = useState<UserFeatureResponseModel[]>(
    [],
  );
  const [partnerInfo] = useState<Array<BaseFilterListResponseModel>>([]);
  const [serviceOptions, setServiceOptions] = useState<
    BaseFilterListResponseModel[]
  >([]);
  const [userMenu, setUserMenu] = useState<Array<BasePermissionMenu>>([]);
  const hasUserMenu = !isEmpty(userMenu);

  const [menuConfig, setMenuConfig] =
    useState<SidebarMenuConfigModel>(undefined);

  const checkLanguage = async () => {
    const savedLanguage = productCache?.getCache(CacheKeyEnum.language);
    const userInfo = productCache?.getCache(CacheKeyEnum.user);
    if (
      savedLanguage === "en" ||
      userInfo?.role?.code === UserRoleEnum.partnerForeign
    ) {
      setLanguage("en");
      productCache?.saveCache({
        key: CacheKeyEnum.language,
        product: "en",
        needExpired: false,
      });
      strings.setLanguage("en");
    } else {
      setLanguage("vi");
      productCache?.saveCache({
        key: CacheKeyEnum.language,
        product: "vi",
        needExpired: false,
      });
      strings.setLanguage("vi");
    }
  };

  const resetState = () => {
    setUserFeature([]);
    setMenuConfig(undefined);
    setUser({});
    setConfig({});
    setServiceOptions([]);
  };

  const applyStartUpResponse = (result?: AppStartUpResponseEntity) => {
    if (!result) return;

    if (result?.user) {
      setUser(result.user);
    }

    if (result?.userMenu) {
      setUserMenu(result.userMenu);
    }

    if (result?.userFeature) {
      setUserFeature(result.userFeature);
    }

    if (result?.menuConfig) {
      setMenuConfig(result.menuConfig);
    }

    if (result?.serviceOptions) {
      const serviceCodes = result?.serviceOptions?.map((item) => item?.code);
      const mappedServices = Object?.entries(strings.GlobalService)
        ?.filter(([filteredKey]) => serviceCodes?.includes(filteredKey))
        ?.map(([key, val]) => ({
          name: val,
          code: key,
        }));
      setServiceOptions(mappedServices);
    }
  };

  const runAppStartUp = async (props?: {
    pathname: string;
    userInfo?: LoginResponseModel;
    isForceRefresh?: boolean;
    isUserReload?: boolean;
    language?: string;
    needBuildSidebar?: boolean;
    taskKeys?: string[];
  }) => {
    const {
      userInfo,
      isForceRefresh = false,
      language: selectedLanguage,
      needBuildSidebar = true,
      isUserReload = false,
      taskKeys,
      pathname,
    } = props || {};
    const useCase = AppStartUpInjection.getUseCase();

    useCase.resetTasks();
    const result = await useCase.runTasks(
      new AppStartUpRequestEntity({
        user: userInfo,
        pathname,
        language: selectedLanguage || language,
        isForceRefresh,
        needBuildSidebar,
        taskKeys,
        isUserReload,
        onBackgroundCompleted: (backgroundResult) => {
          applyStartUpResponse(backgroundResult);
        },
      }),
    );
    applyStartUpResponse(result);
    return result;
  };

  const mapPartnerFromUser = (partner: Array<string>) => {
    const mappedCache = productCache?.getCache(CacheKeyEnum.partnerOfUser);
    if (mappedCache && !isEmpty(mappedCache)) {
      return mappedCache;
    } else {
      const mappedPartner = partner?.map((item) => ({
        partnerRefCode: StringFormat.parseNoteString(item)?.["partnerRefCode"],
        providerCode: StringFormat.parseNoteString(item)?.["providerCode"],
        code: StringFormat.parseNoteString(item)?.["partnerCode"],
        store_code:
          StringFormat.parseNoteString(item)?.["storeCode"] ||
          StringFormat.parseNoteString(item)?.["store_code"],
        store_name:
          StringFormat.parseNoteString(item)?.["storeName"] ||
          StringFormat.parseNoteString(item)?.["store_name"],
      })) as typeof partnerInfo;
      productCache?.saveCache({
        key: CacheKeyEnum.partnerOfUser,
        product: mappedPartner,
        needExpired: false,
      });
      return mappedPartner;
    }
  };

  const getRoles = useCallback(async () => {
    if (!window?.location?.pathname?.includes("/login")) {
      try {
        checkLanguage();
        const cache = productCache?.getCache(CacheKeyEnum.user);
        if (!isEmpty(cache)) {
          mapPartnerFromUser(cache?.partners);
          const isForceRefresh = ActionFormat.isBrowserReload();
          if (isForceRefresh) {
            await runAppStartUp({
              isUserReload: true,
              needBuildSidebar: true,
              pathname: location?.pathname,
            });
          } else {
            await runAppStartUp({
              pathname: location?.pathname,
              isForceRefresh: false,
            });
          }
        } else {
          localStorage?.clear();
          sessionStorage?.clear();
          productCache?.resetCache("");
          getMessage(strings.GlobalMessage.InvalidAccount, 3, "error");
          resetState();
          history("/login");
        }
      } catch (error) {
        console.error(error);
        localStorage?.clear();
        sessionStorage?.clear();
        productCache?.resetCache("");
        resetState();
        getMessage(
          error?.message ?? strings.GlobalMessage.InvalidToken,
          3,
          "error",
        );
        history("/login");
      }
    }
  }, [history]);

  const fetchFilterList = async <R extends Object, P extends Object>(
    props: IFetchDataWithFeature<R, P>,
  ) => {
    const {
      path,
      savedKey,
      isForceRefresh,
      params,
      isNeedFeature = false,
      dataState,
      onError,
    } = props;
    try {
      let res = undefined;
      if (isNeedFeature && !findFeature(path)) {
        !!savedKey && productCache?.resetCache(savedKey);
        return undefined;
      } else {
        if (!isEmpty(productCache?.getCache(savedKey)) && !isForceRefresh) {
          dataState(productCache?.getCache(savedKey));
          return productCache?.getCache(savedKey);
        } else {
          const repo = AppBasedServiceInjection.getFeatureRepo();
          res = await repo.getPathWithFeature<P, R>(path, params);
          if (res?.data) {
            dataState(res?.data);
            if (!isForceRefresh && !!savedKey) {
              productCache.saveCache({
                key: savedKey,
                product: res?.data,
                needExpired: true,
                timeExpired: 10800,
              });
            }
          }
          return res?.data;
        }
      }
    } catch (error) {
      console.error(error);
      !!onError && onError(error);
    }
  };

  const getPartnerInfo = (value: any) => {
    setInfo(value);
  };

  useEffect(() => {
    if (!isEmpty(token) && !isUndefined(token)) {
      getRoles();
    } else {
      localStorage?.clear();
      sessionStorage?.clear();
      productCache?.resetCache("");
      history("/login");
    }
  }, [token]);

  useEffect(() => {
    if (
      !isEmpty(token) &&
      !isUndefined(token) &&
      hasUserMenu &&
      !isEmpty(location?.pathname) &&
      location?.pathname !== "/"
    ) {
      runAppStartUp({
        isForceRefresh: false,
        needBuildSidebar: false,
        taskKeys: ["sync-capabilities"],
        pathname: location?.pathname,
      });
    }
  }, [location?.pathname, token, hasUserMenu]);

  const isAuthenticated = () => {
    return !!token;
  };

  return (
    <LocalContext.Provider
      value={
        {
          user,
          info,
          menuConfig,
          partnerList,
          userFeature,
          globalLoading,
          isLargeScreen,
          language,
          config,
          serviceOptions,
          userMenu,
          versionNumber,
          setUserMenu,
          setLanguage,
          runAppStartUp,
          fetchFilterList,
          getPartnerInfo: getPartnerInfo,
          setGlobalLoading,
          setPartnerList,
          getRoles,
          isAuthenticated,
          resetState,
        } as IContextModel
      }
    >
      {children}
    </LocalContext.Provider>
  );
};

export const AuthenticationContext = () => useContext(LocalContext);
